import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import type { Product } from '../data/products';
type MaterialCatalogCardProps = {
  product: Product;
  categoryName: string;
  isBestSeller: boolean;
  index: number;
};
export function MaterialCatalogCard({
  product,
  categoryName,
  isBestSeller,
  index
}: MaterialCatalogCardProps) {
  // "Good for" line, built from the product's own `bestFor` tags so it stays in
  // sync with the product page. Capped at three so it never wraps the card.
  const useLine = product.bestFor?.length ? product.bestFor.slice(0, 3).join(' · ') : null;
  return <motion.div layout initial={{
    opacity: 0,
    y: 12
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.22,
    delay: Math.min(index, 5) * 0.035
  }} data-id="element-1283" data-mp="2efz5im7u1bmo">
      <Link to={`/shop/${product.slug}`} className="group relative block h-[13.2rem] overflow-hidden rounded-lg border border-stone-200 bg-stone-200 shadow-sm transition-shadow focus:outline-none focus-visible:ring-2 focus-visible:ring-[#04A05C] focus-visible:ring-offset-2 hover:shadow-md sm:h-48 dark:border-white/10 dark:bg-white/10 dark:focus-visible:ring-offset-[#131413]" aria-label={`View ${product.name}, from ${product.pricePerYard} per yard`} data-id="element-1284" data-mp="k3j1zt6f8bli">
        <img src={product.images?.[0] || product.image} alt="" loading={index < 3 ? 'eager' : 'lazy'} decoding="async" className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]" data-id="element-1285" data-mp="3a5mibsgtmr7k" />
        {isBestSeller && <span className="absolute left-0 top-4 px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-wide shadow-sm text-[#fffcf9] bg-[#006239]" data-id="element-1286" data-mp="3k2iss3qa5ner">Most Popular</span>}
        <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-3 bg-[#FBFCFB] px-3 py-2.5 text-stone-650 sm:px-4 dark:bg-black/65 dark:text-white dark:backdrop-blur-[1px]" data-id="element-1287" data-mp="3t50gkmll2o3v">
          <div className="min-w-0 font-sub" data-id="element-1288" data-mp="1ypxuyzznnq68">
            <p className="truncate text-[9px] uppercase tracking-[0.12em] text-stone-900 dark:text-white/80 font-[400] font-[Cabin,_sans-serif]" data-id="element-1289" data-mp="1yqr6sfdh15v8">{categoryName}</p>
            <h3 className="mt-0.5 truncate sm:text-base font-[Cabin,_sans-serif] font-[500] text-[18px]" data-id="element-1290" data-mp="2x33jejjokw4n">{product.name}</h3>
            {/* One-line "what it's for". The card previously showed only a name
                and a price, which assumes the customer already recognizes trade
                names like "5/8 minus" — this is the line that does the teaching
                for someone browsing. */}
            {useLine && <p className="mt-0.5 truncate text-[11px] leading-snug text-stone-600 dark:text-white/70 font-[Cabin,_sans-serif]" data-id="element-1291" data-mp="3g0d381sx4bmf">{useLine}</p>}
          </div>
          <p className="shrink-0 font-[Cabin,_sans-serif] font-[500] text-[14px]" data-id="element-1292" data-mp="3pr7pv7a2td8e">${product.pricePerYard}<span className="ml-0.5 text-xs font-semibold text-stone-700 dark:text-white/55" data-id="element-1293" data-mp="7lcmu531irej">/yd</span></p>
        </div>
      </Link>
    </motion.div>;
}